
import { computed, defineComponent, PropType, reactive, Ref, ref, toRefs, watch } from 'vue'
import ProductNumberIcon from '@/components/Icons/ProductNumberIcon.vue'
import PrintIcon from '@/components/Icons/PrintIcon.vue'
import AddIcon from '@/components/Icons/AddIcon.vue'
import TrashIcon from '@/components/Icons/TrashIcon.vue'
import Clipable from '@/components/Clipable.vue'
import ProductCardVariant from '@/pages/Channels/components/ProductCardVariant.vue'
import { RequestHandler } from '@Protium/vue-ozone-utils/types/utils'
import ToggleNew from '@/components/ToggleNew.vue'
import ProductCardComponent from '@/pages/Channels/components/ProductCardComponent.vue'
import StatusIndicator, { StatusType } from '@/components/StatusIndicator.vue'
import StatusIcon from '@/components/StatusIcon.vue'
import { format } from '@/utils/template'
import { DATE_DISPLAY_FORMAT_HOURS, DATE_FORMAT } from '@/constants'
import { CtCatalogue, CtCatalogueProduct, CtCatalogueStockItem, CtProduct, CtStockItem, CtSupplierCatalogue } from '@/models/interfaces/content-type'
import SimpleLink from '@/components/SimpleLink.vue'
import CatalogueProductVariant from '@/pages/Manage/Catalogues/components/CatalogueProductVariant.vue'
import PenIcon from '@/components/Icons/PenIcon.vue'
import useContentModule from '@/pages/composables/useContentModule'
import { useStore } from 'vuex'
import { EntryConfig } from '@Protium/vue-ozone-utils'
import _, { debounce } from 'lodash'
import ChevronLeftIcon from '@/components/Icons/ChevronLeftIcon.vue'
import { SkuController, SkuCtrl } from '@/pages/Manage/Catalogues/utils'
import Barcode from '@/components/Barcode.vue'
import BarcodeIcon from '@/components/Icons/BarcodeIcon.vue'
import DButton from '@/components/base/DButton.vue'
import { ContentType } from '@/services/directr'
import { BarcodeTypeId } from '@/types/barcode'
import catalogueStockItem from '@/store/modules/content/catalogue-stock-item'

export default defineComponent({
  name: 'SupplierCatalogueStockItemCard',
  components: {
    BarcodeIcon,
    Barcode,
    DButton,
    ChevronLeftIcon,
    PenIcon,
    CatalogueProductVariant,
    SimpleLink,
    StatusIcon,
    StatusIndicator,
    ProductCardComponent,
    ProductCardVariant,
    Clipable,
    TrashIcon,
    AddIcon,
    ProductNumberIcon,
    PrintIcon,
    ToggleNew
  },
  props: {
    editing: {
      type: Boolean,
      default: false,
    },
    catStockItem: {
      type: Object as PropType<CtCatalogueStockItem>,
      required: true,
    },
    stockItems: {
      type: Array as PropType<Array<CtStockItem>>,
      required: true,
    },
    catalogue: {
      type: Object as PropType<CtSupplierCatalogue>,
      required: true,
    },
    request: {
      type: Object as PropType<RequestHandler<any>>,
      required: true
    },
    removing: {
      type: Boolean,
      default: false,
    },
    highlight: {
      type: Boolean,
      default: false,
    },
  },
  emits: ['update:editing', 'update:cat-stock-item', 'update:product-store', 'expand', 'link-stock-item', 'remove', 'cancel'],
  setup (props, { emit }) {
    const $store = useStore()

    const currentStockItem = computed(() => props.stockItems.find(s => s.sys.id === props.catStockItem.id))

    const statusTooltip = computed(() => {
      if (props.catStockItem.meta?.synced_at) {
        if (props.catStockItem.meta?.sync_error?.length) {
          return `Sync error at ${format.date(props.catStockItem.meta?.synced_at, DATE_FORMAT)}: ${props.catStockItem.meta?.sync_error}`
        } else {
          return `Item last synced at ${format.date(props.catStockItem.meta?.synced_at, DATE_FORMAT)} ${String(props.catStockItem.sys?.mirror_id)?.startsWith('#TEMP') ? '(Pending)' : ''}`
        }
      } else {
        return 'Item not yet synced'
      }
    })

    const state = reactive({
      expanded: false,
      linkingStockItem: false,
      status: StatusType,
    })

    watch(() => state.expanded, () => state.expanded ? emit('expand') : null)

    const synced = computed(() => {
      if (!props.catStockItem.active) {
        return 'disabled'
      } else if (props.catStockItem.meta?.synced_at && !props.catStockItem.meta?.sync_error && !String(props.catStockItem.sys?.mirror_id)?.startsWith('#TEMP')) {
        return 'success'
      } else if (props.catStockItem.meta?.sync_error?.length) {
        return 'error'
      } else {
        return 'warn'
      }
    })

    const updateCatalogueStockItem = debounce((updatedCatalogueStockItem) => emit('update:cat-stock-item', updatedCatalogueStockItem), 300, { leading: true })
    const updateCatalogueStockItemField = (field, value) => updateCatalogueStockItem({ ...props.catStockItem, [field]: value })

    const startEditing = ({ expand }) => {
      props.editing || emit('update:editing', true)
      expand && (state.expanded || (state.expanded = true))
    }

    const stockItemModel = ref($store.getters['StockItems/Model'])
    const catStockItemModel = ref($store.getters['CatalogueStockItem/Model'])

    const parsedSearchConfig: Ref<EntryConfig> = ref({ ..._.defaultsDeep({ actions: { edit: true, remove: true, search: false, add: true }, buffer: { resolve: 2, select: ['title', 'sku', 'ean'] } }) })

    function searchStockItems () {
      state.linkingStockItem = true
    }

    function linkStockItem ({ selection }) {
      emit('link-stock-item', props.catStockItem, selection)
      state.linkingStockItem = false
    }

    const syncedAt = computed(() => {
      if (props.catStockItem?.meta?.synced_at) {
        if (props.catStockItem?.meta?.sync_error?.length) {
          return `Sync error at ${format.date(props.catStockItem?.meta?.synced_at, DATE_DISPLAY_FORMAT_HOURS)}: ${props.catStockItem?.meta?.sync_error}`
        } else {
          return `Item last synced at ${format.date(props.catStockItem?.meta?.synced_at, DATE_DISPLAY_FORMAT_HOURS)} ${String(props.catStockItem?.sys?.mirror_id)?.startsWith('#TEMP') ? '(Pending)' : ''}`
        }
      } else {
        return 'Item not yet synced'
      }
    })

    return {
      ...toRefs(state),
      state,
      currentStockItem,
      searchStockItems,
      linkStockItem,
      startEditing,
      stockItemModel,
      catStockItemModel,
      parsedSearchConfig,
      open,
      close,
      synced,
      syncedAt,
      statusTooltip,
      format,
      DATE_FORMAT: DATE_DISPLAY_FORMAT_HOURS,
      showBarcode: reactive({ sku: false, ean: false }),
      BarcodeTypeId,
      updateCatalogueStockItem,
      updateCatalogueStockItemField
    }
  }
})
